/**
 * GroupCertificatesSection Component
 * 
 * Manages certificates linked to a group with ability to add/remove
 * Uses EntitySelector for adding certificates and displays linked certificates with pagination
 * 
 * @component
 */

import { FC, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { 
  FiShield,
  FiPlus,
  FiTrash2,
  FiCheckCircle,
  FiAlertTriangle,
  FiXCircle,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { groupService } from '@/services/groupService';
import { useEntitySelection } from '@/hooks';
import { useToast } from '@/hooks/useToast';
import EntitySelector from '@/components/Common/EntitySelector';
import Button from '@/components/Common/Button';
import Modal from '@/components/Common/Modal';
import ConfirmDialog from '@/components/Common/ConfirmDialog';
import type { CertificateListItem } from '@/types';
import type { EntityColumn } from '@/components/Common/EntitySelector';
import type { GroupCertificatesSectionProps } from './GroupCertificatesSection.types';
import styles from './GroupCertificatesSection.module.css';

/**
 * Get certificate status based on expiration date
 */
const getCertificateStatus = (expiresAt: string): 'valid' | 'expiring' | 'expired' => {
  const expirationDate = new Date(expiresAt);
  const now = new Date();
  const daysUntilExpiration = Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  if (daysUntilExpiration < 0) return 'expired';
  if (daysUntilExpiration <= 30) return 'expiring';
  return 'valid';
};

/**
 * Format expiration date
 */
const formatExpirationDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

/**
 * Get status badge class
 */
const getStatusBadgeClass = (status: string): string => {
  const statusMap: Record<string, string> = {
    valid: styles.statusValid,
    expiring: styles.statusExpiring,
    expired: styles.statusExpired,
  };
  return statusMap[status] || styles.statusValid;
};

/**
 * Get status icon
 */
const getStatusIcon = (status: string) => {
  const iconProps = { size: 14 };
  const statusIcons: Record<string, JSX.Element> = {
    valid: <FiCheckCircle {...iconProps} />,
    expiring: <FiAlertTriangle {...iconProps} />,
    expired: <FiXCircle {...iconProps} />,
  };
  return statusIcons[status] || <FiCheckCircle {...iconProps} />;
};

/**
 * Get status label
 */
const getStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    valid: 'Válido',
    expiring: 'Expirando',
    expired: 'Expirado',
  };
  return labels[status] || status;
};

/**
 * Certificate columns configuration for EntitySelector
 * IMPORTANT: Defined outside component to prevent re-creation on each render
 */
const certificateColumns: EntityColumn<CertificateListItem>[] = [
  {
    key: 'alias',
    label: 'Alias',
    width: '35%',
  },
  {
    key: 'expiresAt',
    label: 'Expira em',
    width: '30%',
    render: (value) => (
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <FiCalendar size={14} />
        {formatExpirationDate(value)}
      </span>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    width: '35%',
    align: 'center',
    render: (_, item) => {
      const status = getCertificateStatus(item.expiresAt);
      return (
        <span className={`${styles.statusBadge} ${getStatusBadgeClass(status)}`}>
          {getStatusIcon(status)}
          {getStatusLabel(status)}
        </span>
      );
    },
  },
];

const GroupCertificatesSection: FC<GroupCertificatesSectionProps> = ({
  groupId,
  onUpdate,
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [linkedCertificatesPage, setLinkedCertificatesPage] = useState(1);
  const linkedCertificatesPageSize = 10;
  
  // Confirm dialog state
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [certificateToRemove, setCertificateToRemove] = useState<{ id: string; alias: string } | null>(null);
  
  const queryClient = useQueryClient();
  const toast = useToast();

  /**
   * Fetch linked certificates (paginated)
   */
  const { data: linkedCertificatesData, isLoading: isLoadingLinked } = useQuery({
    queryKey: ['group-certificates', groupId, linkedCertificatesPage],
    queryFn: async () => {
      return groupService.getGroupCertificates(groupId, {
        page: linkedCertificatesPage - 1, // API is 0-indexed
        size: linkedCertificatesPageSize,
      });
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const linkedCertificates = linkedCertificatesData?.data || [];
  const linkedCertificatesPagination = linkedCertificatesData?.pagination;

  /**
   * Entity selection for available certificates (not linked to group)
   * Uses the new endpoint that returns only certificates NOT in the group
   */
  const certificateSelection = useEntitySelection<CertificateListItem>({
    fetchFn: (params) => groupService.getAvailableCertificates(groupId, params),
    queryKey: `available-certificates-${groupId}`,
    initialPageSize: 10,
  });

  /**
   * Link certificates mutation
   */
  const linkMutation = useMutation({
    mutationFn: async (certificateIds: string[]) => {
      // Link all selected certificates
      await Promise.all(
        certificateIds.map((certId) => groupService.linkCertificate(groupId, certId))
      );
    },
    onSuccess: () => {
      toast.success(
        'Certificados vinculados!',
        'Os certificados foram vinculados ao grupo com sucesso.'
      );
      
      // Reset selection and close modal
      certificateSelection.clearSelection();
      setIsAddModalOpen(false);
      
      // Invalidate both linked certificates AND available certificates cache
      queryClient.invalidateQueries({ queryKey: ['group-certificates', groupId] });
      queryClient.invalidateQueries({ queryKey: [`available-certificates-${groupId}`] });
      
      // Force refetch of available certificates to update the list immediately
      certificateSelection.refetch();
      
      if (onUpdate) {
        onUpdate();
      }
    },
    onError: (error) => {
      toast.error(
        'Erro ao vincular certificados',
        error instanceof Error ? error.message : 'Erro desconhecido'
      );
    },
  });

  /**
   * Unlink certificate mutation
   */
  const unlinkMutation = useMutation({
    mutationFn: async (certificateId: string) => {
      await groupService.unlinkCertificate(groupId, certificateId);
    },
    onSuccess: () => {
      toast.success(
        'Certificado removido!',
        'O certificado foi removido do grupo com sucesso.'
      );
      
      // Invalidate both linked certificates AND available certificates cache
      // When we unlink, the certificate becomes available again
      queryClient.invalidateQueries({ queryKey: ['group-certificates', groupId] });
      queryClient.invalidateQueries({ queryKey: [`available-certificates-${groupId}`] });
      
      if (onUpdate) {
        onUpdate();
      }
    },
    onError: (error) => {
      toast.error(
        'Erro ao remover certificado',
        error instanceof Error ? error.message : 'Erro desconhecido'
      );
    },
  });

  /**
   * Handle add certificates
   */
  const handleAddCertificates = () => {
    if (certificateSelection.selectedIds.length === 0) {
      toast.info(
        'Nenhum certificado selecionado',
        'Selecione pelo menos um certificado para vincular.'
      );
      return;
    }
    
    linkMutation.mutate(certificateSelection.selectedIds);
  };

  /**
   * Handle open add modal
   * Refetch available certificates to ensure fresh data
   */
  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
    // Refetch available certificates when modal opens to ensure fresh data
    certificateSelection.refetch();
  };

  /**
   * Handle remove certificate - Open confirm dialog
   */
  const handleRemoveCertificate = (certificateId: string, certificateAlias: string) => {
    setCertificateToRemove({ id: certificateId, alias: certificateAlias });
    setIsConfirmOpen(true);
  };

  /**
   * Handle confirm remove
   */
  const handleConfirmRemove = () => {
    if (certificateToRemove) {
      unlinkMutation.mutate(certificateToRemove.id);
      setIsConfirmOpen(false);
      setCertificateToRemove(null);
    }
  };

  /**
   * Handle cancel remove
   */
  const handleCancelRemove = () => {
    setIsConfirmOpen(false);
    setCertificateToRemove(null);
  };

  return (
    <div className={styles.groupCertificatesSection}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.icon}>
            <FiShield size={20} />
          </div>
          <div>
            <h2 className={styles.title}>Certificados Vinculados</h2>
            <p className={styles.subtitle}>
              {linkedCertificatesPagination?.total || 0} {linkedCertificatesPagination?.total === 1 ? 'certificado vinculado' : 'certificados vinculados'}
            </p>
          </div>
        </div>
        <Button
          variant="primary"
          size="md"
          iconLeft={<FiPlus size={16} />}
          onClick={handleOpenAddModal}
        >
          Adicionar Certificados
        </Button>
      </div>

      {/* Linked Certificates List */}
      <div className={styles.certificatesList}>
        {isLoadingLinked ? (
          <div className={styles.loadingState}>
            <p>Carregando certificados vinculados...</p>
          </div>
        ) : linkedCertificates.length === 0 ? (
          <div className={styles.emptyState}>
            <FiShield size={48} className={styles.emptyIcon} />
            <p className={styles.emptyMessage}>Nenhum certificado vinculado a este grupo</p>
            <p className={styles.emptyHint}>Clique em "Adicionar Certificados" para começar</p>
          </div>
        ) : (
          <>
            {/* Certificates Table */}
            <div className={styles.tableWrapper}>
              <table className={styles.certificatesTable}>
                <thead>
                  <tr>
                    <th>Alias</th>
                    <th>Expira em</th>
                    <th>Status</th>
                    <th style={{ width: '80px', textAlign: 'center' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedCertificates.map((certificate) => {
                    const status = getCertificateStatus(certificate.expiresAt);
                    
                    return (
                      <tr key={certificate.id}>
                        <td>
                          <div className={styles.certificateNameCell}>
                            <FiShield size={16} className={styles.certificateIcon} />
                            <span className={styles.certificateName}>{certificate.alias}</span>
                          </div>
                        </td>
                        <td>
                          <span className={styles.expirationDate}>
                            <FiCalendar size={14} />
                            {formatExpirationDate(certificate.expiresAt)}
                          </span>
                        </td>
                        <td>
                          <span className={`${styles.statusBadge} ${getStatusBadgeClass(status)}`}>
                            {getStatusIcon(status)}
                            {getStatusLabel(status)}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className={styles.removeButton}
                            onClick={() => handleRemoveCertificate(certificate.id, certificate.alias)}
                            disabled={unlinkMutation.isPending}
                            title="Remover certificado"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination for linked certificates */}
            {linkedCertificatesPagination && linkedCertificatesPagination.totalPages > 1 && (
              <div className={styles.pagination}>
                <button
                  onClick={() => setLinkedCertificatesPage((prev) => Math.max(1, prev - 1))}
                  disabled={!linkedCertificatesPagination.hasPrev}
                  className={styles.paginationButton}
                >
                  <FiChevronLeft size={16} />
                  Anterior
                </button>
                <span className={styles.paginationInfo}>
                  Página {linkedCertificatesPage} de {linkedCertificatesPagination.totalPages}
                </span>
                <button
                  onClick={() => setLinkedCertificatesPage((prev) => Math.min(linkedCertificatesPagination.totalPages, prev + 1))}
                  disabled={!linkedCertificatesPagination.hasNext}
                  className={styles.paginationButton}
                >
                  Próxima
                  <FiChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Certificates Modal */}
      <Modal
        open={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          certificateSelection.clearSelection();
        }}
        title="Adicionar Certificados ao Grupo"
        description="Selecione os certificados que deseja vincular a este grupo"
        size="lg"
      >
        <div className={styles.modalContent}>
          <EntitySelector
            entities={certificateSelection.entities}
            selectedIds={certificateSelection.selectedIds}
            columns={certificateColumns}
            onSelectionChange={certificateSelection.setSelectedIds}
            onSearch={certificateSelection.handleSearch}
            searchValue={certificateSelection.searchTerm}
            currentPage={certificateSelection.currentPage}
            totalPages={certificateSelection.totalPages}
            onPageChange={certificateSelection.handlePageChange}
            isLoading={certificateSelection.isLoading}
            error={certificateSelection.error}
            searchPlaceholder="Buscar certificados por alias..."
            emptyMessage="Nenhum certificado encontrado"
            selectionMode="multiple"
            showSelectAll={true}
          />
          
          <div className={styles.modalActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setIsAddModalOpen(false);
                certificateSelection.clearSelection();
              }}
              disabled={linkMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleAddCertificates}
              disabled={linkMutation.isPending || certificateSelection.selectedIds.length === 0}
              loading={linkMutation.isPending}
            >
              Adicionar {certificateSelection.selectedIds.length > 0 && `(${certificateSelection.selectedIds.length})`}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={isConfirmOpen}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        title="Remover Certificado"
        message={`Tem certeza que deseja remover o certificado "${certificateToRemove?.alias}" deste grupo?`}
        confirmText="Remover"
        cancelText="Cancelar"
        variant="danger"
        isLoading={unlinkMutation.isPending}
      />
    </div>
  );
};

export default GroupCertificatesSection;
